import React, { useEffect, useRef, useState } from "react";
import {
  FileVideo,
  LinkIcon,
  Loader2,
  UploadCloud,
  X,
} from "lucide-react";

const MAX_FILE_BYTES = 2 * 1024 * 1024 * 1024;
const ACCEPTED_EXTENSIONS = ["mp4", "mov", "m4v", "webm"];

function formatBytes(bytes) {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 MB";
  return `${(bytes / 1024 / 1024).toFixed(bytes > 100 * 1024 * 1024 ? 0 : 1)} MB`;
}

function getYouTubeID(value) {
  try {
    const url = new URL(value);

    if (url.hostname === "youtu.be") {
      return url.pathname.split("/").filter(Boolean)[0] || null;
    }

    if (url.hostname.endsWith("youtube.com")) {
      if (url.pathname === "/watch") return url.searchParams.get("v");
      if (url.pathname.startsWith("/shorts/")) return url.pathname.split("/")[2];
      if (url.pathname.startsWith("/embed/")) return url.pathname.split("/")[2];
    }
  } catch {
    return null;
  }

  return null;
}

export default function UploadSection({
  file,
  setFile,
  videoURL,
  setVideoURL,
  operation,
  onFileUpload,
  onURLImport,
}) {
  const inputRef = useRef(null);
  const [previewURL, setPreviewURL] = useState("");
  const [dragging, setDragging] = useState(false);
  const [validationError, setValidationError] = useState("");

  const busy = operation !== "idle";
  const youtubeID = getYouTubeID(videoURL.trim());
  const importing = operation === "importing";
  const uploading = operation === "uploading";

  useEffect(() => {
    if (!file) {
      setPreviewURL("");
      return undefined;
    }

    const objectURL = URL.createObjectURL(file);
    setPreviewURL(objectURL);

    return () => URL.revokeObjectURL(objectURL);
  }, [file]);

  const chooseFile = (selectedFile) => {
    setValidationError("");

    if (!selectedFile) return;

    const extension = selectedFile.name.split(".").pop()?.toLowerCase();
    const looksLikeVideo =
      selectedFile.type.startsWith("video/") ||
      ACCEPTED_EXTENSIONS.includes(extension);

    if (!looksLikeVideo) {
      setValidationError("Choose an MP4, MOV, M4V or WebM video.");
      return;
    }

    if (selectedFile.size <= 0) {
      setValidationError("The selected video is empty.");
      return;
    }

    if (selectedFile.size > MAX_FILE_BYTES) {
      setValidationError("The maximum upload size is 2 GB.");
      return;
    }

    setVideoURL("");
    setFile(selectedFile);
  };

  const clearFile = () => {
    setFile(null);
    setValidationError("");
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragging(false);
    if (!busy) chooseFile(event.dataTransfer.files?.[0]);
  };

  return (
    <section className="mx-auto w-full max-w-4xl" aria-labelledby="source-title">
      <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm sm:p-7">
        <div className="mb-6">
          <h2 id="source-title" className="text-lg font-semibold text-gray-950">
            Add your source video
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Upload a video file or import a public YouTube video.
          </p>
        </div>

        {!file && (
          <div className="space-y-4">
            <div className="relative">
              <LinkIcon
                className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400"
                aria-hidden="true"
              />
              <input
                type="url"
                inputMode="url"
                autoComplete="url"
                aria-label="YouTube video URL"
                placeholder="Paste a YouTube URL"
                value={videoURL}
                disabled={busy}
                onChange={(event) => {
                  setValidationError("");
                  setVideoURL(event.target.value);
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && youtubeID && !busy) {
                    event.preventDefault();
                    onURLImport();
                  }
                }}
                className="w-full rounded-xl border border-gray-200 bg-white py-3 pl-12 pr-4 text-sm text-gray-900 outline-none transition placeholder:text-gray-400 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 disabled:bg-gray-50"
              />
            </div>

            {videoURL.trim() && !youtubeID && (
              <p className="text-sm text-amber-700" role="status">
                Enter a valid YouTube watch, Shorts or youtu.be URL.
              </p>
            )}

            {youtubeID && (
              <div className="overflow-hidden rounded-xl border border-gray-200 bg-gray-50 sm:flex">
                <img
                  src={`https://img.youtube.com/vi/${youtubeID}/hqdefault.jpg`}
                  alt="YouTube video thumbnail"
                  className="aspect-video w-full bg-gray-200 object-cover sm:w-56"
                />
                <div className="flex flex-1 items-center justify-between gap-4 p-4">
                  <div>
                    <p className="font-medium text-gray-900">YouTube video</p>
                    <p className="mt-1 text-xs text-gray-500">
                      We’ll import the source before analysis.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={onURLImport}
                    disabled={busy}
                    className="inline-flex shrink-0 items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {importing && <Loader2 className="h-4 w-4 animate-spin" />}
                    {importing ? "Importing…" : "Import video"}
                  </button>
                </div>
              </div>
            )}

            {!videoURL.trim() && (
              <>
                <div className="flex items-center gap-3 text-xs font-medium uppercase tracking-wider text-gray-400">
                  <span className="h-px flex-1 bg-gray-200" />
                  or upload a file
                  <span className="h-px flex-1 bg-gray-200" />
                </div>

                <button
                  type="button"
                  disabled={busy}
                  onClick={() => inputRef.current?.click()}
                  onDragEnter={(event) => {
                    event.preventDefault();
                    setDragging(true);
                  }}
                  onDragOver={(event) => event.preventDefault()}
                  onDragLeave={() => setDragging(false)}
                  onDrop={handleDrop}
                  className={`flex w-full flex-col items-center justify-center rounded-xl border-2 border-dashed px-6 py-10 text-center transition ${
                    dragging
                      ? "border-indigo-500 bg-indigo-50"
                      : "border-gray-300 bg-gray-50 hover:border-indigo-400 hover:bg-indigo-50/50"
                  } disabled:cursor-not-allowed disabled:opacity-60`}
                >
                  <span className="mb-3 rounded-full bg-indigo-100 p-3 text-indigo-600">
                    <UploadCloud className="h-6 w-6" aria-hidden="true" />
                  </span>
                  <span className="text-sm font-semibold text-gray-900">
                    Choose a video or drag it here
                  </span>
                  <span className="mt-1 text-xs text-gray-500">
                    MP4, MOV, M4V or WebM · maximum 2 GB
                  </span>
                </button>
              </>
            )}
          </div>
        )}

        <input
          ref={inputRef}
          type="file"
          className="sr-only"
          accept="video/mp4,video/quicktime,video/x-m4v,video/webm,.mp4,.mov,.m4v,.webm"
          disabled={busy}
          onChange={(event) => chooseFile(event.target.files?.[0])}
        />

        {file && (
          <div className="overflow-hidden rounded-xl border border-gray-200">
            <div className="relative bg-black">
              <video
                src={previewURL}
                controls
                preload="metadata"
                className="aspect-video max-h-[430px] w-full object-contain"
              />
              <button
                type="button"
                onClick={clearFile}
                disabled={busy}
                aria-label="Remove selected video"
                className="absolute right-3 top-3 rounded-full bg-black/70 p-2 text-white transition hover:bg-black disabled:opacity-50"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="flex flex-col justify-between gap-4 p-4 sm:flex-row sm:items-center">
              <div className="flex min-w-0 items-center gap-3">
                <FileVideo className="h-5 w-5 shrink-0 text-indigo-600" />
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-gray-900">
                    {file.name}
                  </p>
                  <p className="text-xs text-gray-500">{formatBytes(file.size)}</p>
                </div>
              </div>

              <button
                type="button"
                onClick={onFileUpload}
                disabled={busy}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {uploading && <Loader2 className="h-4 w-4 animate-spin" />}
                {uploading ? "Uploading…" : "Upload video"}
              </button>
            </div>
          </div>
        )}

        {validationError && (
          <p
            className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
            role="alert"
          >
            {validationError}
          </p>
        )}
      </div>
    </section>
  );
}
